import useSWR from 'swr';
import { fetchService } from '../../utils/fetchService';
import { RequestError } from '../../utils/RequestError';
import { DashboardAnalyticsDateRangeDays, DashboardAnalyticsMetrics } from './dashboardAnalytics';

/**
 * Same SWR pattern as useFetchService in fetchService.ts: key `[path, accessToken]`,
 * fetcher `() => fetchService(...)` so SWR's spread-args invocation is not mistaken for a tuple.
 * 404 returns null (useFetchService alone would surface an error).
 * `isLoading` is true for the initial fetch while `data` is still undefined (see Dashboards).
 */
export function useDashboardAnalyticsMetrics(dateRangeDays: DashboardAnalyticsDateRangeDays, accessToken: string) {
  const path = `/eval/analytics/metrics?dateRange=${dateRangeDays}`;
  const shouldFetch = Boolean(accessToken);
  const key = shouldFetch ? [path, accessToken] : null;

  const fetcher = async (): Promise<DashboardAnalyticsMetrics | null> => {
    try {
      return (await fetchService(path, accessToken)) as DashboardAnalyticsMetrics;
    } catch (e) {
      if (e instanceof RequestError && e.status === 404) {
        return null;
      }
      throw e;
    }
  };

  const { data, error } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const isLoading = Boolean(key && data === undefined && !error);

  return { data, error, isLoading };
}
